import React from 'react';
import {
  List,
  Datagrid,
  TextField,
  NumberField,
  DateField,
  TopToolbar,
  CreateButton,
  ExportButton,
  SearchInput,
  SelectInput,
  DateInput,
  EditButton,
  ShowButton,
  DeleteButton,
  ReferenceField,
  FunctionField,
  useRedirect,
} from 'react-admin';
import { Chip, Box } from '@mui/material';
import { Receipt as InvoiceIcon } from '@mui/icons-material';
import { CustomEmptyState } from '../../components/CustomEmptyState';
import { PageTitle } from '../../components/PageTitle';

const InvoiceFilters = [
  <SearchInput key="search" source="q" placeholder="Search invoices..." alwaysOn />,
  <SelectInput
    key="status"
    source="status"
    label="Status"
    choices={[
      { id: 'draft', name: 'Draft' },
      { id: 'sent', name: 'Sent' },
      { id: 'paid', name: 'Paid' },
      { id: 'overdue', name: 'Overdue' },
      { id: 'cancelled', name: 'Cancelled' },
    ]}
    alwaysOn
  />,
  <DateInput key="dateFrom" source="dateFrom" label="From Date" />,
  <DateInput key="dateTo" source="dateTo" label="To Date" />,
];

const ListActions = () => (
  <TopToolbar>
    <CreateButton />
    <ExportButton />
  </TopToolbar>
);

const StatusChip = ({ record }: { record: any }) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft':
        return 'default';
      case 'sent':
        return 'info';
      case 'paid':
        return 'success';
      case 'overdue':
        return 'error';
      case 'cancelled':
        return 'warning';
      default:
        return 'default';
    }
  };

  return (
    <Chip
      label={record.status?.toUpperCase() || 'UNKNOWN'}
      color={getStatusColor(record.status) as any}
      size="small"
    />
  );
};

export const InvoiceList = () => {
  const redirect = useRedirect();

  const handleCreateInvoice = () => {
    redirect('/invoices/create');
  };

  return (
    <Box>
      <PageTitle
        title="Invoices"
        titleKey="pages.invoices"
        subtitle="Manage billing and payments"
        icon={<InvoiceIcon />}
      />
      <List
        actions={<ListActions />}
        perPage={25}
        sort={{ field: 'createdAt', order: 'DESC' }}
        empty={<CustomEmptyState resource="invoices" onAction={handleCreateInvoice} />}
        sx={{ '& .RaList-main': { mt: 0 } }}
      >
      <Datagrid rowClick="show" bulkActionButtons={false}>
        <TextField source="invoiceNumber" label="Number" />
        <ReferenceField source="customerId" reference="customers" link="show">
          <TextField source="displayName" />
        </ReferenceField>
        <TextField source="title" />
        <NumberField source="totalAmount" options={{ style: 'currency', currency: 'USD' }} />
        <FunctionField
          source="status"
          label="Status"
          render={(record: any) => <StatusChip record={record} />}
        />
        <DateField source="dueDate" label="Due Date" />
        <DateField source="createdAt" label="Created" showTime />
        <EditButton />
        <ShowButton />
        <DeleteButton />
      </Datagrid>
    </List>
    </Box>
  );
};
